import { FC, useEffect } from 'react';
import { useToast } from '../ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { Notification } from '@/hooks/useNotifications';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { getNotificationStyle } from './notificationStyles';

interface NotificationEvent extends CustomEvent {
  detail: Notification & { icon?: string };
}

export const NotificationListener: FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Suscribirse a notificaciones en tiempo real
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const notification = payload.new as Notification;
          let icon = '📬';
          let title = notification.title;
          let description = notification.message;

          // Personalizar la notificación según el tipo
          switch (notification.type) {
            case 'comment':
              icon = '💬';
              title = 'Nuevo comentario';
              description = `${notification.metadata?.user_name || 'Alguien'} ha comentado en tu publicación`;
              break;
            case 'system':
              if (notification.metadata?.action === 'moderation') {
                icon = '⚠️';
                title = 'Moderación de contenido';
              } else {
                icon = '🔔';
              }
              break;
            default:
              break;
          }

          // Mostrar la notificación
          toast({
            title,
            description,
            variant: "default",
            className: "bg-white/95 backdrop-blur-sm border-2 border-primary/20",
            duration: 5000,
            action: (
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getNotificationStyle(notification.type).bgColor}`}>
                  {icon}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-primary">
                    {title}
                  </p>
                  <p className="text-sm text-gray-700">
                    {description}
                  </p>
                </div>
              </div>
            ),
          });

          // Reproducir sonido de notificación
          const audio = new Audio('/sounds/notification.mp3');
          audio.play().catch(() => {}); // Ignorar errores si el navegador bloquea el audio
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, toast]);

  return null;
};
